import { baseApi } from '@/shared/api'
import type {
  ChangeQuantityBody,
  ChangeQuantityResult,
  ConfirmDraftLinesBody,
  ConfirmDraftLinesResult,
  InvoiceCommentBody,
  InvoiceEmailBody,
  InvoiceList,
  InvoicesQuery,
  OrderLineList,
  OrderLinesQuery,
  OrdersActing,
  RemoveLinesBody,
  RemoveLinesResult,
  WriteOffBody,
  WriteOffResult,
} from '../model/lines'
import type {
  InvoiceActionBody,
  OrderCommandState,
  OrdersChangeResult,
  ReserveFromListBody,
  ReserveFromListResult,
} from '../model/operations'

/** Модификатор уходит строкой запроса и у записи: тело — полезная нагрузка действия. */
const actingParams = (acting: OrdersActing | undefined) => ({
  ...(acting?.client !== undefined ? { client: acting.client } : {}),
  ...(acting?.account !== undefined ? { account: acting.account } : {}),
})

const LINES = [{ type: 'Order' as const, id: 'LINES' }]

/** Команда не закрыта — опрашиваем; ответ 1С по резерву/дозаказу может ждать долго. */
export const ORDER_COMMAND_POLL_MS = 10_000

export const orderLineApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Строки резервов и счетов. Сортировка, страницы и `total` — сервер; у менеджера без
     * `account` — вся фирма, в строке `accountId` владельца.
     */
    getOrderLines: build.query<OrderLineList, OrderLinesQuery>({
      query: (params) => ({ url: '/orders', params }),
      providesTags: LINES,
    }),

    /** Счета для дозаказа («Добавить в»). */
    getInvoicesForAppend: build.query<InvoiceList, InvoicesQuery>({
      query: (params) => ({ url: '/orders/invoices', params }),
      providesTags: [{ type: 'Order', id: 'INVOICES' }],
    }),

    changeLineQuantity: build.mutation<
      ChangeQuantityResult,
      { acting?: OrdersActing } & ChangeQuantityBody
    >({
      query: ({ acting, ...body }) => ({
        url: '/orders/lines/quantity',
        method: 'PATCH',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: LINES,
    }),

    /**
     * Подтверждение строк черновика заказного каталога РБ (`?section=draft-rb`): комментарий
     * ложится в строки, по каждому счёту в 1С уходит файл fire-and-forget (ответа не ждём),
     * позиции становятся подтверждёнными и уходят из черновиков. Redis-блокировка: повтор того же
     * набора, пока первый не отработал, — `404`.
     */
    confirmDraftLines: build.mutation<
      ConfirmDraftLinesResult,
      { acting?: OrdersActing } & ConfirmDraftLinesBody
    >({
      query: ({ acting, ...body }) => ({
        url: '/orders/lines/confirm',
        method: 'POST',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: LINES,
    }),

    removeOrderLines: build.mutation<
      RemoveLinesResult,
      { acting?: OrdersActing } & RemoveLinesBody
    >({
      query: ({ acting, ...body }) => ({
        url: '/orders/lines',
        method: 'DELETE',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: LINES,
    }),

    commentInvoice: build.mutation<
      void,
      { invoiceNo: string; acting?: OrdersActing } & InvoiceCommentBody
    >({
      query: ({ invoiceNo, acting, ...body }) => ({
        url: `/orders/${encodeURIComponent(invoiceNo)}/comment`,
        method: 'POST',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: LINES,
    }),

    /** Счёт на почту: у клиента адрес обязателен, у менеджера — почта учётной записи клиента. */
    emailInvoice: build.mutation<
      void,
      { invoiceNo: string; acting?: OrdersActing } & InvoiceEmailBody
    >({
      query: ({ invoiceNo, acting, ...body }) => ({
        url: `/orders/${encodeURIComponent(invoiceNo)}/email`,
        method: 'POST',
        params: actingParams(acting),
        body,
      }),
    }),

    /** Списание счетов целиком: строки удаляются сразу, в 1С уходит файл. */
    writeOffInvoices: build.mutation<WriteOffResult, { acting?: OrdersActing } & WriteOffBody>({
      query: ({ acting, ...body }) => ({
        url: '/orders/write-off',
        method: 'POST',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: LINES,
    }),

    /**
     * Объединить счета: по строкам выбранных счетов в 1С уходит заявка (команда `orders.merge`).
     * Ответа 1С не присылает — команда закроется по сроку (`expired`), результат придёт
     * уведомлением (`command.expired`). Поэтому опрос не заводим: инвалидируем список и очередь
     * команд, остальное — на уведомлениях. `422 order.merge.bad-range` — строки вне диапазона.
     */
    mergeOrders: build.mutation<OrdersChangeResult, { acting?: OrdersActing } & InvoiceActionBody>({
      query: ({ acting, ...body }) => ({
        url: '/orders/merge',
        method: 'POST',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: [...LINES, { type: 'Command', id: 'LIST' }],
    }),

    /** Разделить счёт: механика как у объединения, `422 order.split.bad-range`. */
    splitOrder: build.mutation<OrdersChangeResult, { acting?: OrdersActing } & InvoiceActionBody>({
      query: ({ acting, ...body }) => ({
        url: '/orders/split',
        method: 'POST',
        params: actingParams(acting),
        body,
      }),
      invalidatesTags: [...LINES, { type: 'Command', id: 'LIST' }],
    }),

    /**
     * Резерв списком из буфера: текст уходит как есть, парами «код,количество» его разбирает бэк
     * (на клиенте не парсим — правило 11). Без `invoiceNo` — новый резерв, с ним — дозаказ в счёт
     * «Добавить в». Ответ `201` всегда несёт `commandId`: дальше опрос `getOrderCommand`.
     * `422 list.unparsed`/`list.odd-count`, `409 RESERVE_LIST_REJECTED` — ветки отказа.
     */
    reserveFromList: build.mutation<ReserveFromListResult, ReserveFromListBody>({
      query: (body) => ({ url: '/orders/from-list', method: 'POST', body }),
      invalidatesTags: [
        ...LINES,
        { type: 'Order', id: 'INVOICES' },
        { type: 'Command', id: 'LIST' },
      ],
    }),

    /**
     * Состояние команды обмена с 1С (`GET /v1/commands/{id}`). Тег `Command` — тот же, что
     * инвалидирует §0 по WS-событию: рефетч приходит сокетом, опрос остаётся запасным путём.
     */
    getOrderCommand: build.query<OrderCommandState, string>({
      query: (id) => ({ url: `/commands/${encodeURIComponent(id)}` }),
      providesTags: (_res, _err, id) => [{ type: 'Command', id }],
    }),
  }),
})

export const {
  useGetOrderLinesQuery,
  useGetInvoicesForAppendQuery,
  useChangeLineQuantityMutation,
  useConfirmDraftLinesMutation,
  useRemoveOrderLinesMutation,
  useCommentInvoiceMutation,
  useEmailInvoiceMutation,
  useWriteOffInvoicesMutation,
  useMergeOrdersMutation,
  useSplitOrderMutation,
  useReserveFromListMutation,
  useGetOrderCommandQuery,
} = orderLineApi
